<template>
  <transition
    v-if="!noContent"
    :name="transitionName"
  >
    <div
      v-show="isActive"
      v-cloak
      ref="overlay"
      class="overlay critical-hide"
      :class="[
        classContainer,
        $blockClass(blockClass),
        responsiveType,
        `${responsiveType}--${responsiveDirection}`,
      ]"
      :data-namespace="namespace"
    >
      <div
        v-if="template"
        ref="scrollElement"
        class="overlay__container"
        :class="[$blockClass(blockClass, 'container'), $blockClass(responsiveType, 'container')]"
        @scroll="emitScroll"
      >
        <div
          v-if="$slots.title || showClose"
          class="overlay__header"
          :class="[
            {
              'overlay__header--no-title': !$slots.title,
              [$blockClass(blockClass, 'header', 'no-title')]: !$slots.title,
              [$blockClass(responsiveType, 'header', 'no-title')]: !$slots.title,
            },
            $blockClass(blockClass, 'header'),
            $blockClass(responsiveType, 'header'),
          ]"
        >
          <div
            v-if="$slots.title"
            class="overlay__title"
            :class="[$blockClass(blockClass, 'title'), $blockClass(responsiveType, 'title')]"
          >
            <slot name="title" />
          </div>

          <div
            v-if="showClose"
            class="overlay__close-container"
            :class="[
              $blockClass(blockClass, 'close-container'),
              $blockClass(responsiveType, 'close-container'),
            ]"
          >
            <button
              class="overlay__close icon-button"
              :class="[$blockClass(blockClass, 'close'), $blockClass(responsiveType, 'close')]"
              type="button"
              @click.prevent="
                () => {
                  close('button')
                  track({
                    event: `${source || namespace}_close`,
                  })
                }
              "
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />

              <slot name="close">
                <icon-close />
              </slot>
            </button>
          </div>
        </div>

        <div
          class="overlay__body"
          :class="[
            $blockClass(blockClass, 'body'),
            $blockClass(responsiveType, 'body'),
            { 'overlay__body--no-margin': !$slots.title },
          ]"
        >
          <slot
            v-bind="self"
            name="body"
          />
        </div>
      </div>

      <!-- If template prop is false then just render body slot -->
      <div
        v-else
        ref="scrollElement"
        class="overlay__container"
        :class="[$blockClass(blockClass, 'container'), $blockClass(responsiveType, 'container')]"
        @scroll="emitScroll"
      >
        <slot
          v-bind="self"
          name="body"
        />
      </div>

      <teleport
        :disabled="!teleport"
        to="body"
      >
        <transition name="fade">
          <button
            v-if="isActive && showOverlay"
            class="window-overlay"
            :class="namespace"
            tabindex="-1"
            type="button"
            aria-hidden="true"
            @click.prevent="
              () => {
                close('overlay')
                track({
                  event: `${source || namespace}_close`,
                })
              }
            "
          />
        </transition>
      </teleport>
    </div>
  </transition>
</template>

<script>
/**
 * Vue: Overlay (overlay)
 * -----------------------------------------------------------------------------
 * Base template for overlays (drawers and modals).
 * - Add `js-overlay="scrollElement"` to element in <slot> to pass that as the
 *   element to disableBodyScroll instead of `.overlay__container`.
 *
 * @param {Boolean} [autoQueue] - Automatically opened overlays are queued.
 * @param {Number} [autoShow] - Time in ms to wait before auto-showing overlay.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [classContainer] - Class applied to container.
 * @param {Boolean} [confirmation] - Only close using the `confirmClose`
 * function. If set to true, the overlay will be set to a `stuck` state until
 * the `confirmClose` function is called.
 * @param {String} direction - Open animation direction set using transition
 * styles, `up` or `down` for `modal` and `left` or `right` for `drawer`,
 * supports a combination in the format [mobile direction]-[desktop direction].
 * @param {Boolean} [disableScroll] - Trigger `disableBodyScroll` on open.
 * @param {boolean} [globalEsc] - Exclude component from catch all escape close
 * unless it's the latest active overlay
 * @param {String} namespace - Unique namespace for overlay, reference when
 * opening/closing the overlay.
 * @param {Boolean} [noContent] - Displays no content, only window overlay, you
 * must manually disable scroll and trap focus in the component which triggers
 * this overlay.
 * @param {Boolean} [showClose] - Show the close button, useful for when you
 * want to bind custom events to when the overlay closes.
 * @param {Boolean} [showOverlay] - Show the window overlay?
 * @param {Boolean} [teleport] - Teleport window overlay to end of <body>.
 * @param {Boolean} [template] - Render with default templating.
 * @param {String} type - Type of overlay, `modal`, `drawer`, or `shelf`,
 * supports a combination in the format [mobile type]-[desktop type].
 * @param {boolean} [unique] - Close other overlays if this overlay is open.
 */
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconClose from '~icons/directional-navigation/close.svg'

import { track } from '~/helpers/datalayer-actions'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Overlay',

  components: {
    IconClose,
  },

  props: {
    autoQueue: {
      type: Boolean,
      default: true,
    },
    autoShow: {
      type: Number,
      default: 0,
    },
    blockClass: {
      type: String,
      default: '',
    },
    classContainer: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'up',
      required: true,
      validator: (value) => {
        return value
          .split('-')
          .every((part) => ['up', 'down', 'left', 'right', 'fade'].includes(part))
      },
    },
    disableScroll: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    noContent: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
    template: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'modal',
      required: true,
      validator: (value) => {
        return value.split('-').every((part) => ['drawer', 'modal', 'shelf'].includes(part))
      },
    },
    unique: {
      type: Boolean,
      default: true,
    },
    confirmation: {
      type: Boolean,
      default: false,
    },
    globalEsc: {
      type: Boolean,
      default: true,
    },
    preventScrollOnFocus: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update:overlay': (payload) => typeof payload === 'object',
    'confirmation-close': () => true,
  },

  data() {
    return {
      callback: {
        button: false,
        overlay: false,
      },
      cnvsGlobal: true,
      scrollElement: false,
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getElementInFocusTrap: 'accessibility/getElementInFocusTrap',
      getFirstElement: 'accessibility/getFirstElement',
      getOverlay: 'overlays/getOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      hydrated: (state) => state.index.hydrated,
      isMobile: (state) => state.index.screen.mobile,
      previousElement: (state) => state.accessibility.previousElement,
      active: (state) => state.overlays.active,
    }),

    /**
     * Compute overlay active state.
     * @returns {Boolean}
     */
    isActive() {
      if (this.unique) {
        return this.getActiveOverlay?.namespace === this.namespace
      }

      return this.active.some((overlay) => overlay.namespace === this.namespace && !overlay.queue)
    },

    /**
     * Compute responsive direction based on mobile.
     * @retursn {String}
     */
    responsiveDirection() {
      if (!this.direction.includes('-')) {
        return this.direction
      }

      return this.isMobile ? this.direction.split('-')[0] : this.direction.split('-')[1]
    },

    /**
     * Compute responsive type based on mobile.
     * @returns {String}
     */
    responsiveType() {
      if (!this.type.includes('-')) {
        return this.type
      }

      return this.isMobile ? this.type.split('-')[0] : this.type.split('-')[1]
    },

    /**
     * Compute overlay API.
     * @returns {Object}
     */
    self() {
      return {
        ...this.$data,
        ...this.$props,
        isActive: this.isActive,
        addCallback: this.addCallback,
        close: this.close,
        confirmClose: this.confirmClose,
        open: (args) => this.openOverlay({ namespace: this.namespace, ...args }),
        update: this.update,
      }
    },

    /**
     * Compute transition name based on type.
     * @returns {String}
     */
    transitionName() {
      if (this.direction === 'fade') {
        return 'fade'
      }

      const type = this.responsiveType === 'modal' ? 'fade' : 'slide'
      return `${type}-${this.responsiveDirection}`
    },
  },

  watch: {
    /**
     * Watch hydrated state to start auto-show countdown.
     */
    hydrated() {
      this.setAutoShow()
    },

    /**
     * Watch active state.
     * - Separate logic for event binding.
     */
    async isActive() {
      if (this.noContent) {
        return
      }

      /**
       * If active then:
       * - Set previous element (to focus on close).
       * - Disable body scroll (if applicable).
       * - Reset scroll element scroll position.
       * - Trap focus inside overlay element.
       * - Focus first trap focus element.
       */
      if (this.isActive) {
        this.setPreviousElement(document.activeElement)

        if (this.disableScroll) {
          this.getScrollElement()
          disableBodyScroll(this.scrollElement, this.bodyScrollOptions())
        }

        await this.$nextTick()

        if (this.scrollElement) {
          this.scrollElement.scrollTo(0, 0)
        }

        this.updateFocusTrap()
        return
      }

      if (this.disableScroll) {
        if (this.scrollElement) {
          enableBodyScroll(this.scrollElement)
        } else {
          clearAllBodyScrollLocks()
        }
      }

      /**
       * If no other active overlay when closing then clear focus trap.
       */
      if (!this.getActiveOverlay) {
        this.clearFocusTrap()
      }

      /**
       * Set focus back to previous element if set.
       * - Also reset previous element state to false.
       */
      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return
      }

      cnvs.Focus(this.previousElement, this.preventScrollOnFocus)
      this.setPreviousElement()
    },

    /**
     * Emit API when it's updated
     */
    self: {
      handler(overlay) {
        this.$emit('update:overlay', overlay)
      },
      immediate: true,
    },

    /**
     * Update focus trap on the active overlay when multiple overlays are open and one is closed
     */
    getActiveOverlay(current, before) {
      if (current?.namespace === before?.namespace || current?.namespace !== this.namespace) {
        return
      }

      this.updateFocusTrap({ currentElement: this.previousElement })
    },
  },

  mounted() {
    this.setEventBusListeners()
    this.setAutoShow()
  },

  methods: {
    track,

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      queueOverlay: 'overlays/queue',
      setStuck: 'overlays/setStuck',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Emit scroll event
     * @param event - Scroll event
     */
    emitScroll(event) {
      cnvs.EventBus.emit(`overlay:scroll:${this.namespace}`, {
        scrollTop: event.target.scrollTop,
      })
    },

    /**
     * Get scroll element.
     */
    getScrollElement() {
      if (!this.$refs.scrollElement) {
        this.scrollElement = false
        return
      }

      this.scrollElement =
        this.$refs.scrollElement.querySelector('[js-overlay="scrollElement"]') ||
        this.$refs.scrollElement
    },

    /**
     * Set modal to autoshow.
     */
    async setAutoShow() {
      if (!this.autoShow || !this.hydrated) {
        return
      }

      await cnvs.Sleep(this.autoShow)

      if (this.autoQueue) {
        this.queueOverlay({ namespace: this.namespace })
        return
      }

      this.openOverlay({ namespace: this.namespace })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      const escapeEvent = this.globalEsc ? 'cnvs:esc' : `cnvs:esc:${this.namespace}`
      cnvs.EventBus.listen(escapeEvent, () => {
        this.close('esc')
      })

      cnvs.EventBus.listen('overlay:update', (payload) => {
        let namespace = payload
        let args = payload

        if (typeof payload === 'object') {
          namespace = payload.namespace
        } else {
          args = {}
        }

        if (!this.isActive || namespace !== this.namespace) {
          return
        }

        this.update(args)
      })
    },

    /**
     * Update body scroll element and focus trap.
     * @param {Object} [args] - Arguments passed on overlay API update.
     */
    async update(args) {
      await this.$nextTick()
      this.updateFocusTrap(args)
      this.updateBodyScroll(args)
    },

    /**
     * Update focus trap using Vuex.
     * @param {HTMLElement} [currentElement] - Current focussed element to
     * maintain focus on whilst updating focus trap, if it still exists.
     * @param {Object} [focusElements] - Additional focus trap elements, these
     * are used instead of any focusElements defined when opening the overlay.
     * See overlays.js for the JSDoc definition of focusElements.
     */
    updateFocusTrap({ currentElement, focusElements } = {}) {
      const overlay = this.getOverlay(this.namespace)

      const usedfocusElements = focusElements ? focusElements : overlay?.focusElements || {}

      const config = {
        ...usedfocusElements,
        container: [this.$refs.overlay],
      }

      if (Array.isArray(usedfocusElements?.container)) {
        config.container = usedfocusElements.prepend
          ? [...usedfocusElements.container, this.$refs.overlay]
          : [this.$refs.overlay, ...usedfocusElements.container]
      }

      this.setFocusTrap(config)

      /**
       * Update focussed element.
       * - Have to use timeout to wait for animation to complete as Safari
       *   doesn't support `preventScroll`.
       * - Without timeout the focus would jump instead of smoothly scroll.
       */
      const elementToFocus = this.getElementToFocus(currentElement)

      if (!elementToFocus) {
        return
      }

      setTimeout(() => {
        elementToFocus.focus({ preventScroll: true })
      }, this.$timing('normal'))
    },

    /**
     * Get element to focus inside focus trap.
     * @param {HTMLElement} [currentElement] - Current focussed element to
     * maintain focus on whilst updating focus trap, if it still exists.
     * @returns {HTMLElement|Null}
     */
    getElementToFocus(currentElement) {
      if (!currentElement || !this.getElementInFocusTrap(currentElement)) {
        return this.getFirstElement
      }

      return currentElement
    },

    /**
     * Update body scroll element.
     */
    updateBodyScroll({ scrollElement } = {}) {
      if (scrollElement || this.scrollElement) {
        enableBodyScroll(scrollElement || this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      this.getScrollElement()

      if (cnvs.storybook && !this.scrollElement && !scrollElement) {
        return
      }

      if (this.disableScroll) {
        disableBodyScroll(scrollElement || this.scrollElement)
      }
    },

    /**
     * Define body scroll options.
     * - Adds support for [js-body-scroll-lock-ignore] attribute which allows
     *   element to maintain scrolling when disableBodyScroll is enabled.
     * @returns {Object}
     */
    bodyScrollOptions() {
      return {
        // eslint-disable-next-line consistent-return
        allowTouchMove: (element) => {
          const ignoredElement = element.closest('[js-body-scroll-lock-ignore]')
          return ignoredElement
        },
      }
    },

    /**
     * Add callback function to run after close.
     * @param {Function} callback - Function to use as callback on close.
     * @param {String} [type] - Optional, either `button` or `overlay` to define
     * when callback should be run.
     */
    addCallback(callback, type) {
      if (!type) {
        this.callback.button = callback
        this.callback.overlay = callback
        return
      }

      this.callback[type] = callback
    },

    /**
     * Handle overlay close.
     * @param {String} type - Type of close, `button`, `esc`, 'confirm', or
     * `overlay`.
     */
    close(type) {
      if (this.confirmation && type !== 'confirm') {
        this.setStuck({
          namespace: this.namespace,
          stuck: true,
        })
        this.$emit('confirmation-close')
        return
      }

      this.closeOverlay(this.namespace)

      if (typeof this.callback[type] !== 'function') {
        return
      }

      this.callback[type]()
    },

    /**
     * Unstuck this overlay and close if confirmed
     * @param {Boolean} confirmed
     */
    confirmClose(confirmed) {
      this.setStuck({
        namespace: this.namespace,
        stuck: false,
      })

      if (confirmed) {
        this.close('confirm')
      }
    },
  },
}
</script>
